/// Twilight finder — for a given UT date and observer position, find
/// the moments the Sun's altitude crosses the standard thresholds:
///
///   sunrise / sunset       altitude = −0.833°  (refraction + SD)
///   civil twilight         altitude = −6°
///   nautical twilight      altitude = −12°  (star sights)
///   astronomical twilight  altitude = −18°
///
/// We solve geometrically: the Sun's altitude at any UT depends on
/// (lat, lon, time) via sin(h) = sin(Lat)·sin(Dec) + cos(Lat)·cos(Dec)·cos(LHA).
/// Bisecting over the day finds the moment the altitude crosses each
/// target. Returns `null` for an event that doesn't happen at that
/// latitude on that day (polar day / polar night).

import { sunPosition } from "./sun";
import { DEG, RAD, normalize360 } from "./time";

export interface TwilightTimes {
  /// The UTC date we computed times for (00:00 UT of that day).
  date: Date;
  sunrise: Date | null;
  sunset: Date | null;
  civilDawn: Date | null;
  civilDusk: Date | null;
  nauticalDawn: Date | null;
  nauticalDusk: Date | null;
  astronomicalDawn: Date | null;
  astronomicalDusk: Date | null;
}

/// Sun's altitude at the observer at a given instant. Degrees.
export function sunAltitudeAt(
  date: Date,
  latDeg: number,
  lonDeg: number,
): number {
  const { ghaDeg, decDeg } = sunPosition(date);
  const lhaDeg = normalize360(ghaDeg + lonDeg);
  const sinH =
    Math.sin(latDeg * DEG) * Math.sin(decDeg * DEG) +
    Math.cos(latDeg * DEG) * Math.cos(decDeg * DEG) * Math.cos(lhaDeg * DEG);
  return Math.asin(Math.max(-1, Math.min(1, sinH))) * RAD;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const HALF_DAY_MS = 12 * 60 * 60 * 1000;

function utcMidnight(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
}

/// Find the UT instant within the 24 h after `midnight` when the Sun's
/// altitude crosses `targetAltDeg` rising (sun coming up) or setting.
/// Returns null when no crossing exists in that half of the day
/// (polar day / polar night, or the Sun simply doesn't reach the
/// threshold at that latitude on that date).
function findCrossing(
  midnight: Date,
  latDeg: number,
  lonDeg: number,
  targetAltDeg: number,
  type: "rising" | "setting",
): Date | null {
  const noon = new Date(midnight.getTime() + HALF_DAY_MS);
  const nextMidnight = new Date(midnight.getTime() + DAY_MS);

  // Bracket: rising events live in [midnight, noon]; setting in
  // [noon, nextMidnight]. The Sun rises monotonically through the
  // morning and falls monotonically through the afternoon at typical
  // latitudes, so a single sign-change in the bracket is the rule.
  let lo = type === "rising" ? midnight : noon;
  let hi = type === "rising" ? noon : nextMidnight;

  const altLo = sunAltitudeAt(lo, latDeg, lonDeg) - targetAltDeg;
  const altHi = sunAltitudeAt(hi, latDeg, lonDeg) - targetAltDeg;
  // No sign change → no crossing in this half.
  if (altLo * altHi > 0) return null;

  // Bisect. 30 iterations on a 12-hour window → millisecond-grade
  // precision, far below any pedagogically meaningful tolerance.
  for (let i = 0; i < 30; i++) {
    const mid = new Date((lo.getTime() + hi.getTime()) / 2);
    const altMid = sunAltitudeAt(mid, latDeg, lonDeg) - targetAltDeg;
    if (altMid * altLo > 0) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return new Date((lo.getTime() + hi.getTime()) / 2);
}

/// Compute sunrise / sunset and the three twilight times for a given
/// UTC date and observer position. Any event that doesn't happen at
/// that latitude / date returns `null`.
export function twilightTimes(
  date: Date,
  latDeg: number,
  lonDeg: number,
): TwilightTimes {
  const midnight = utcMidnight(date);
  return {
    date: midnight,
    sunrise: findCrossing(midnight, latDeg, lonDeg, -0.833, "rising"),
    sunset: findCrossing(midnight, latDeg, lonDeg, -0.833, "setting"),
    civilDawn: findCrossing(midnight, latDeg, lonDeg, -6, "rising"),
    civilDusk: findCrossing(midnight, latDeg, lonDeg, -6, "setting"),
    nauticalDawn: findCrossing(midnight, latDeg, lonDeg, -12, "rising"),
    nauticalDusk: findCrossing(midnight, latDeg, lonDeg, -12, "setting"),
    astronomicalDawn: findCrossing(midnight, latDeg, lonDeg, -18, "rising"),
    astronomicalDusk: findCrossing(midnight, latDeg, lonDeg, -18, "setting"),
  };
}
